/** option key 元数据：类型、敏感、status 刷新、只读 */

export type OptionValueType = 'string' | 'bool' | 'number' | 'json-object' | 'json-array'

export interface OptionMetaEntry {
  type: OptionValueType
  sensitive?: boolean
  /** 保存后需刷新 GET /api/status */
  affectsStatus?: boolean
  /** 保存后建议刷新用户充值信息 */
  affectsTopupInfo?: boolean
  /** 后端合成只读，禁止 PUT */
  readonly?: boolean
  label?: string
}

/** 保存后触发 /api/status 刷新的 option key */
export const STATUS_REFRESH_KEYS = new Set([
  'Price',
  'StripeUnitPrice',
  'QuotaPerUnit',
  'DefaultUseAutoGroup',
  'ServerAddress',
  'display_in_currency',
  'quota_display_type',
  'usd_exchange_rate',
  'custom_currency_exchange_rate',
  'custom_currency_symbol',
])

/** 保存后建议刷新充值页配置的 key */
export const TOPUP_INFO_REFRESH_KEYS = new Set([
  'Price',
  'MinTopUp',
  'StripeUnitPrice',
  'StripeMinTopUp',
  'PayAddress',
  'EpayId',
  'EpayKey',
  'StripeApiSecret',
  'StripeWebhookSecret',
  'StripePriceId',
  'CreemApiKey',
  'CreemWebhookSecret',
  'CreemProducts',
  'CreemTestMode',
  'WaffoEnabled',
  'WaffoApiKey',
  'WaffoPrivateKey',
  'WaffoSandboxApiKey',
  'WaffoSandboxPrivateKey',
  'WaffoMerchantId',
  'WaffoUnitPrice',
  'WaffoMinTopUp',
  'WaffoPayMethods',
  'PayMethods',
  'payment_setting.amount_options',
  'payment_setting.amount_discount',
  'TopupGroupRatio',
])

/** 只读 key，禁止出现在 save() 的 keys 参数中 */
export const READONLY_OPTION_KEYS = new Set(['CompletionRatioMeta'])

export const OPTION_META: Record<string, OptionMetaEntry> = {
  CompletionRatioMeta: { type: 'json-object', readonly: true, label: '补全倍率元信息（只读）' },
  Price: { type: 'number', affectsStatus: true, affectsTopupInfo: true, label: '充值价格' },
  StripeUnitPrice: { type: 'number', affectsStatus: true, affectsTopupInfo: true, label: 'Stripe 单价' },
  DefaultUseAutoGroup: { type: 'bool', affectsStatus: true, label: '默认 auto 分组' },
  ServerAddress: { type: 'string', affectsStatus: true, label: '服务根地址' },
  EpayKey: { type: 'string', sensitive: true, affectsTopupInfo: true, label: '易支付密钥' },
  StripeApiSecret: { type: 'string', sensitive: true, affectsTopupInfo: true, label: 'Stripe API Secret' },
  StripeWebhookSecret: { type: 'string', sensitive: true, affectsTopupInfo: true, label: 'Stripe Webhook Secret' },
  WaffoPrivateKey: { type: 'string', sensitive: true, affectsTopupInfo: true, label: 'Waffo 私钥' },
  WaffoSandboxPrivateKey: { type: 'string', sensitive: true, affectsTopupInfo: true, label: 'Waffo Sandbox 私钥' },
  'model_deployment.ionet.api_key': { type: 'string', sensitive: true, label: 'io.net API Key' },
  'payment_setting.amount_options': { type: 'json-array', affectsTopupInfo: true, label: '预设充值金额' },
  'payment_setting.amount_discount': { type: 'json-object', affectsTopupInfo: true, label: '充值折扣' },
  PayMethods: { type: 'json-array', affectsTopupInfo: true, label: '充值方式' },
  ModelRequestRateLimitGroup: { type: 'json-object', label: '分组速率限制' },
  'alert_setting.dingtalk_webhook_url': { type: 'string', sensitive: true, label: '钉钉 Webhook' },
  'alert_setting.dingtalk_secret': { type: 'string', sensitive: true, label: '钉钉 Secret' },
  'alert_setting.alert_enabled': { type: 'bool', label: '启用告警' },
  'alert_setting.error_log_enabled': { type: 'bool', label: '错误消费日志' },
  'alert_setting.structured_log_enabled': { type: 'bool', label: '结构化运行日志' },
  'alert_setting.cert_monitor_enabled': { type: 'bool', label: '证书到期巡检' },
  'alert_setting.cert_monitor_domains': { type: 'string', label: '证书巡检域名' },
}

export function filterSaveableKeys(keys: string[]): string[] {
  return keys.filter((k) => !READONLY_OPTION_KEYS.has(k))
}

export function keysNeedStatusRefresh(keys: string[]): boolean {
  return keys.some((k) => STATUS_REFRESH_KEYS.has(k) || OPTION_META[k]?.affectsStatus)
}

export function keysNeedTopupRefresh(keys: string[]): boolean {
  return keys.some((k) => TOPUP_INFO_REFRESH_KEYS.has(k) || OPTION_META[k]?.affectsTopupInfo)
}

export function formatSaveScopeLabel(keys: string[]): string {
  const saveable = filterSaveableKeys(keys)
  if (saveable.length === 0) return '保存'
  if (saveable.length === 1) {
    const label = OPTION_META[saveable[0]]?.label
    return label ? `保存「${label}」` : `保存 1 项`
  }
  return `保存 ${saveable.length} 项`
}
